import { BigNumber } from 'bignumber.js'
import createDependableResource from 'utils/zustand/createDependableResource'
import { getVvsBoostFarmContract, getCraftsmanContract } from 'utils/contractHelpers'
import { multicallv2Encoded } from 'utils/multicall'
import farms from 'config/constants/farms'
import type { AwaitedReturnType } from 'utils/types'

export interface BoostableFarmInfo {
  farmId: number
  active: boolean
  configIsFinished: boolean
  boostFarmExpired: boolean
  depositTokenPid: number
  boostAllocation: BigNumber

  accTokenPerShare: BigNumber
  depositToken: string
  lastRewardBlock: BigNumber
  totalShares: BigNumber
}
interface BoostableFarmsInfo {
  boostableFarmsIds: number[]
  boostableFarms: BoostableFarmInfo[]
  boostableFarmsFromFarmId: Record<number, BoostableFarmInfo>
}

const fetchBoostableFarmsInfo = async (): Promise<BoostableFarmsInfo> => {
  const configBoostableFarms = farms.filter((f) => f.enableBoost)
  const vvsBoostFarmContract = getVvsBoostFarmContract()
  const craftsmanContract = getCraftsmanContract()

  const vvsBoostFarmPoolInfos = await multicallv2Encoded<
    AwaitedReturnType<typeof vvsBoostFarmContract['getPoolInfo']>[]
  >(
    configBoostableFarms.map((farmConfig) => [
      vvsBoostFarmContract,
      'getPoolInfo',
      vvsBoostFarmContract.interface.encodeFunctionData('getPoolInfo', [farmConfig.pid]),
    ]),
  )

  const boostableFarmInfos = vvsBoostFarmPoolInfos.map((info, index) => ({
    farmId: configBoostableFarms[index].pid,
    info,
  }))

  const boostableFarmOnCraftsmanPoolInfos = await multicallv2Encoded<
    AwaitedReturnType<typeof craftsmanContract['poolInfo']>[]
  >(
    boostableFarmInfos.map(({ info }) => [
      craftsmanContract,
      'poolInfo',
      craftsmanContract.interface.encodeFunctionData('poolInfo', [info.depositTokenPid]),
    ]),
  )

  const boostableFarms = boostableFarmInfos.map(({ farmId, info }, index) => {
    const boostableFarmOnCraftsmanPoolInfo = boostableFarmOnCraftsmanPoolInfos[index]
    const farmConfig = configBoostableFarms[index]

    return {
      farmId,
      active: info.active,
      configIsFinished: Boolean(farmConfig.isFinished),
      boostFarmExpired: Boolean(farmConfig.boostFarmExpired),
      depositTokenPid: info.depositTokenPid.toNumber(),
      boostAllocation: new BigNumber(boostableFarmOnCraftsmanPoolInfo.allocPoint.toString()),

      accTokenPerShare: new BigNumber(info.accTokenPerShare.toString()),
      depositToken: info.depositToken,
      lastRewardBlock: new BigNumber(info.lastRewardBlock.toString()),
      totalShares: new BigNumber(info.totalShares.toString()),
    }
  })

  return {
    boostableFarmsIds: boostableFarms.map((f) => f.farmId),
    boostableFarms,
    boostableFarmsFromFarmId: Object.fromEntries(boostableFarms.map((f) => [f.farmId, f])),
  }
}

export const useBoostableFarmsInfo = createDependableResource<BoostableFarmsInfo>({
  initValue: {
    boostableFarmsIds: [],
    boostableFarms: [],
    boostableFarmsFromFarmId: {},
  },
  getValue: fetchBoostableFarmsInfo,
  dependResources: [],
  dbgName: 'BoostableFarmsInfo',
})

export const useBoostableFarmInfo = (farmId?: number) => {
  return useBoostableFarmsInfo((s) => s.value.boostableFarmsFromFarmId[farmId])
}
